import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, LifeBuoy } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export interface SidebarLinkItem {
  label: string;
  href: string;
  icon: LucideIcon;
  active: boolean;
  badge?: number;
}

export interface SidebarGroup {
  label: string;
  icon: LucideIcon;
  active: boolean;
  badge?: number;
  items: Array<{
    label: string;
    href: string;
    icon: LucideIcon;
    active: boolean;
    badge?: number;
  }>;
}

interface AppSidebarProps {
  collapsed: boolean;
  mobileOpen: boolean;
  onToggleCollapse: () => void;
  onNavigate: () => void;
  links: SidebarLinkItem[];
  trailingLinks: SidebarLinkItem[];
  myRecords: SidebarGroup | null;
  settingsLink: SidebarLinkItem | null;
  afterSettingsLinks?: SidebarLinkItem[];
  footer?: React.ReactNode;
}

function CountBadge({ count, collapsed }: { count: number; collapsed: boolean }) {
  if (count <= 0) return null;
  if (collapsed) {
    return <span className="absolute right-2 top-1.5 h-2 w-2 rounded-full bg-red-500" />;
  }
  return (
    <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

function itemClass(active: boolean, collapsed: boolean) {
  return `relative flex items-center gap-3 rounded-lg py-2.5 text-sm font-medium transition-colors ${
    collapsed ? 'justify-center px-2' : 'px-3'
  } ${
    active
      ? 'bg-[#2F6BFF] text-white shadow-sm'
      : 'text-[#D5DDED] hover:bg-white/10 hover:text-white'
  }`;
}

const AppSidebar: React.FC<AppSidebarProps> = ({
  collapsed,
  mobileOpen,
  onToggleCollapse,
  onNavigate,
  links,
  trailingLinks,
  myRecords,
  settingsLink,
  afterSettingsLinks = [],
  footer,
}) => {
  const showLabels = mobileOpen || !collapsed;
  const rail = !showLabels;
  const [recordsOpen, setRecordsOpen] = useState(myRecords?.active ?? false);
  const [helpOpen, setHelpOpen] = useState(false);
  const recordsRef = useRef<HTMLDivElement>(null);
  const helpRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (myRecords?.active) setRecordsOpen(true);
  }, [myRecords?.active]);

  useEffect(() => {
    if (!recordsOpen && !helpOpen) return;
    const onPointer = (event: MouseEvent) => {
      const target = event.target as Node;
      if (recordsOpen && rail && recordsRef.current && !recordsRef.current.contains(target)) {
        setRecordsOpen(false);
      }
      if (helpOpen && helpRef.current && !helpRef.current.contains(target)) {
        setHelpOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointer);
    return () => document.removeEventListener('mousedown', onPointer);
  }, [recordsOpen, helpOpen, rail]);

  return (
    <aside
      className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col bg-[#0B1F4B] text-white transition-transform duration-200 ${
        mobileOpen ? 'translate-x-0' : '-translate-x-full'
      } md:relative md:z-40 md:translate-x-0 md:transition-[width] ${
        collapsed ? 'md:w-[76px]' : 'md:w-[248px]'
      }`}
    >
      <div className="relative flex h-[72px] shrink-0 items-center border-b border-white/10 px-4">
        <Link
          to="/"
          onClick={onNavigate}
          className={`flex min-w-0 items-center ${rail ? 'mx-auto' : ''}`}
          title="Staffly"
        >
          {rail ? (
            <img src="/staffly-mark-light.png" alt="Staffly" className="h-9 w-9 object-contain" />
          ) : (
            <img
              src="/staffly-logo-light.png"
              alt="Staffly"
              className="h-9 w-auto max-w-[180px] object-contain object-left"
            />
          )}
        </Link>
        <button
          type="button"
          onClick={onToggleCollapse}
          className="absolute -right-3 top-1/2 z-20 hidden h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 shadow-sm hover:text-slate-800 md:flex"
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          <ChevronLeft className={`h-3.5 w-3.5 transition-transform ${collapsed ? 'rotate-180' : ''}`} strokeWidth={2.25} />
        </button>
      </div>

      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overflow-x-hidden px-3 py-4">
        {links.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavigate}
              title={rail ? item.label : undefined}
              className={itemClass(item.active, rail)}
            >
              <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
              {showLabels && <span className="truncate">{item.label}</span>}
              <CountBadge count={item.badge ?? 0} collapsed={rail} />
            </Link>
          );
        })}

        {myRecords && (
          <div className="relative" ref={recordsRef}>
            <button
              type="button"
              title={rail ? myRecords.label : undefined}
              onClick={() => setRecordsOpen((open) => !open)}
              className={`${itemClass(myRecords.active && !recordsOpen, rail)} w-full`}
              aria-expanded={recordsOpen}
            >
              <myRecords.icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
              {showLabels && <span className="truncate">{myRecords.label}</span>}
              {showLabels && !recordsOpen && (myRecords.badge ?? 0) > 0 && (
                <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                  {(myRecords.badge ?? 0) > 99 ? '99+' : myRecords.badge}
                </span>
              )}
              {showLabels && (
                <ChevronDown
                  className={`${!recordsOpen && (myRecords.badge ?? 0) > 0 ? '' : 'ml-auto'} h-4 w-4 shrink-0 text-current/70 transition-transform ${recordsOpen ? 'rotate-180' : ''}`}
                  strokeWidth={2}
                />
              )}
              {!showLabels && <CountBadge count={myRecords.badge ?? 0} collapsed />}
            </button>

            {recordsOpen && showLabels && (
              <div className="mt-1 space-y-0.5 pl-7">
                {myRecords.items.map((child) => {
                  const ChildIcon = child.icon;
                  return (
                  <Link
                    key={child.href}
                    to={child.href}
                    onClick={onNavigate}
                    className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                      child.active
                        ? 'bg-white/15 font-medium text-white'
                        : 'text-[#C5D0E6] hover:bg-white/10 hover:text-white'
                    }`}
                  >
                    <ChildIcon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    <span className="truncate">{child.label}</span>
                    {child.badge != null && child.badge > 0 && (
                      <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                        {child.badge > 99 ? '99+' : child.badge}
                      </span>
                    )}
                  </Link>
                  );
                })}
              </div>
            )}

            {recordsOpen && rail && (
              <div className="absolute left-full top-0 z-50 ml-3 w-56 rounded-xl border border-slate-200 bg-white py-1 text-slate-800 shadow-xl">
                <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-400">
                  {myRecords.label}
                </div>
                {myRecords.items.map((child) => {
                  const ChildIcon = child.icon;
                  return (
                  <Link
                    key={child.href}
                    to={child.href}
                    onClick={() => {
                      setRecordsOpen(false);
                      onNavigate();
                    }}
                    className={`flex items-center gap-2 px-3 py-2 text-sm ${
                      child.active ? 'bg-blue-50 font-medium text-blue-700' : 'hover:bg-slate-50'
                    }`}
                  >
                    <ChildIcon className="h-4 w-4 shrink-0" strokeWidth={1.75} />
                    <span className="truncate">{child.label}</span>
                    {child.badge != null && child.badge > 0 && (
                      <span className="ml-auto flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-semibold text-white">
                        {child.badge > 99 ? '99+' : child.badge}
                      </span>
                    )}
                  </Link>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {trailingLinks.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavigate}
              title={rail ? item.label : undefined}
              className={itemClass(item.active, rail)}
            >
              <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
              {showLabels && <span className="truncate">{item.label}</span>}
              <CountBadge count={item.badge ?? 0} collapsed={rail} />
            </Link>
          );
        })}

        {settingsLink && (
          <Link
            to={settingsLink.href}
            onClick={onNavigate}
            title={rail ? settingsLink.label : undefined}
            className={itemClass(settingsLink.active, rail)}
          >
            <settingsLink.icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
            {showLabels && <span className="truncate">{settingsLink.label}</span>}
          </Link>
        )}

        {afterSettingsLinks.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              onClick={onNavigate}
              title={rail ? item.label : undefined}
              className={itemClass(item.active, rail)}
            >
              <Icon className="h-5 w-5 shrink-0" strokeWidth={1.75} />
              {showLabels && <span className="truncate">{item.label}</span>}
            </Link>
          );
        })}

        <div className="relative" ref={helpRef}>
          <button
            type="button"
            title={rail ? 'Help & Support' : undefined}
            onClick={() => setHelpOpen((open) => !open)}
            className={`${itemClass(false, rail)} w-full`}
            aria-expanded={helpOpen}
          >
            <LifeBuoy className="h-5 w-5 shrink-0" strokeWidth={1.75} />
            {showLabels && <span className="truncate">Help & Support</span>}
          </button>
          {helpOpen && (
            <div
              className={`z-50 rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-600 shadow-xl ${
                rail ? 'absolute left-full top-0 ml-3 w-56' : 'mt-1'
              }`}
            >
              <p className="font-medium text-slate-800">Help & Support</p>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">
                For account changes, open My Account. For access or product help, contact your administrator.
              </p>
              <Link
                to="/my-account"
                onClick={() => {
                  setHelpOpen(false);
                  onNavigate();
                }}
                className="mt-2 inline-block text-sm font-medium text-blue-600 hover:text-blue-700"
              >
                Go to My Account
              </Link>
            </div>
          )}
        </div>
      </nav>
      {footer}
    </aside>
  );
};

export default AppSidebar;
